import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  acceptInvitation,
  acceptInvitationDirect,
  declineInvitation,
  getInvitation,
  getMyInvitations,
} from '~/services/api/invitations';
import type { InvitationResponse, PendingInvitationItem } from '~/services/api/invitations';
import { logger } from '~/utils/logger';

import type { AcceptInvitationResponse } from '~/types';

import { useAuth } from './useAuth';

/**
 * React Query mutation for accepting an invitation with email token.
 * Invalidates invitations and organizations queries on success.
 *
 * @returns Mutation object with acceptInvitation function
 */
export function useAcceptInvitation(): UseMutationResult<
  AcceptInvitationResponse,
  Error,
  { invitationId: string; token: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invitationId, token }: { invitationId: string; token: string }) =>
      acceptInvitation(invitationId, token),
    onSuccess: () => {
      logger.info({ component: 'useAcceptInvitation' }, 'Invitation accepted successfully');
      // Invalidate invitations list
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
      // Invalidate organizations to refresh org selector (matches TenantContext query key)
      void queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
    onError: (error: Error) => {
      logger.error({ component: 'useAcceptInvitation' }, 'Failed to accept invitation', error);
    },
  });
}

/**
 * React Query mutation for accepting an invitation directly (no email token).
 * Invalidates invitations and organizations queries on success.
 *
 * Useful for local dev without email infrastructure or streamlined UX.
 *
 * @returns Mutation object with acceptInvitationDirect function
 */
export function useAcceptInvitationDirect(): UseMutationResult<
  AcceptInvitationResponse,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) => acceptInvitationDirect(invitationId),
    onSuccess: () => {
      logger.info({ component: 'useAcceptInvitationDirect' }, 'Invitation accepted successfully');
      // Invalidate invitations list
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
      // Invalidate organizations to refresh org selector (matches TenantContext query key)
      void queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
    onError: (error: Error) => {
      logger.error(
        { component: 'useAcceptInvitationDirect' },
        'Failed to accept invitation',
        error,
      );
    },
  });
}

/**
 * React Query mutation for declining an invitation.
 * Invalidates invitations query on success.
 *
 * @returns Mutation object with declineInvitation function
 */
export function useDeclineInvitation(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) => declineInvitation(invitationId),
    onSuccess: () => {
      logger.info({ component: 'useDeclineInvitation' }, 'Invitation declined successfully');
      // Invalidate invitations list
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
    onError: (error: Error) => {
      logger.error({ component: 'useDeclineInvitation' }, 'Failed to decline invitation', error);
    },
  });
}

/**
 * React Query hook for fetching a single invitation by ID and token.
 *
 * Used for the invitation acceptance page where the user arrives via email link.
 *
 * @param invitationId - Invitation ID from URL
 * @param token - JWT token from URL query params
 * @returns Query result with invitation details
 */
export function useInvitation(
  invitationId: string | undefined,
  token: string,
): UseQueryResult<InvitationResponse> {
  return useQuery<InvitationResponse>({
    queryKey: ['invitation', invitationId, token],
    queryFn: () => {
      if (!invitationId) {
        throw new Error('Invitation ID is required');
      }
      return getInvitation(invitationId, token);
    },
    enabled: !!invitationId && !!token,
    retry: false, // Don't retry if token is invalid/expired
    staleTime: Infinity, // Invitation details won't change
  });
}

/**
 * React Query hook for fetching pending invitations for the current user.
 *
 * Only fetches invitations when user is authenticated.
 * Automatically refetches periodically to catch new invitations.
 *
 * @returns Query result with invitations data
 */
export function useInvitations(): UseQueryResult<PendingInvitationItem[]> {
  const { loading, user } = useAuth();

  return useQuery<PendingInvitationItem[]>({
    queryKey: ['invitations', 'pending'],
    queryFn: getMyInvitations,
    enabled: !loading && !!user,
    staleTime: 1000 * 30, // 30 seconds - data considered fresh
    refetchInterval: 1000 * 60 * 2, // Refetch every 2 minutes (better UX without being spammy)
    retry: 1, // Only retry once to avoid spamming backend
  });
}
